import { useRef, useState, useEffect } from "react";
import type { Step } from "../../../shared/types";
import { api } from "../../api";
import { useEditor } from "../DemoEditor";

export function VoiceoverPanel({ step }: { step: Step }) {
  const { setBundle } = useEditor();
  const [isRecording, setIsRecording] = useState(false);
  const [micError, setMicError] = useState<string | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(step.audioKey ? `/api/images/${step.audioKey}` : null);
  const [transcriptText, setTranscriptText] = useState(step.transcript?.text || "");
  const [audioReviewed, setAudioReviewed] = useState(step.audioReviewed);
  const mediaRecorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedDevice, setSelectedDevice] = useState<string>(localStorage.getItem("voiceover_mic") || "");
  const [eqPreset, setEqPreset] = useState<'male' | 'female'>((localStorage.getItem("voiceover_eq") as 'male' | 'female') || 'male');
  
  const meterFillRef = useRef<HTMLDivElement>(null);
  const peakRef = useRef<HTMLDivElement>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const reqAnimRef = useRef<number>(0);
  const peakHoldRef = useRef<{ val: number; time: number }>({ val: 0, time: 0 });
  const eqNodesRef = useRef<{ hp: BiquadFilterNode; mud: BiquadFilterNode; pres: BiquadFilterNode; deEss: BiquadFilterNode; limiter: DynamicsCompressorNode } | null>(null);

  const applyEqPreset = (preset: 'male' | 'female', nodes: NonNullable<typeof eqNodesRef.current>) => {
    const { hp, mud, pres, deEss, limiter } = nodes;
    if (preset === 'male') {
      hp.frequency.value = 80; hp.Q.value = 0.707;
      mud.frequency.value = 200; mud.Q.value = 1.5; mud.gain.value = -3;
      pres.frequency.value = 3500; pres.Q.value = 1.2; pres.gain.value = 3;
      deEss.frequency.value = 10000; deEss.gain.value = -2;
    } else {
      hp.frequency.value = 110; hp.Q.value = 0.707;
      mud.frequency.value = 300; mud.Q.value = 1.5; mud.gain.value = -2;
      pres.frequency.value = 4500; pres.Q.value = 1.2; pres.gain.value = 4;
      deEss.frequency.value = 10000; deEss.gain.value = -2;
    }
    limiter.threshold.value = -3;
    limiter.knee.value = 8;
    limiter.ratio.value = 10;
    limiter.attack.value = 0.005;
    limiter.release.value = 0.1;
  };

  useEffect(() => {
    if (eqNodesRef.current) applyEqPreset(eqPreset, eqNodesRef.current);
  }, [eqPreset]);

  useEffect(() => {
    setAudioUrl(step.audioKey ? `/api/images/${step.audioKey}` : null);
    setTranscriptText(step.transcript?.text || "");
    setAudioReviewed(step.audioReviewed);
    setMicError(null);
  }, [step.id, step.audioKey, step.transcript, step.audioReviewed]);

  const loadDevices = async () => {
    try {
      const d = await navigator.mediaDevices.enumerateDevices();
      setDevices(d.filter(dev => dev.kind === 'audioinput'));
    } catch (e) {}
  };

  useEffect(() => {
    loadDevices();
  }, []);

  const destRef = useRef<MediaStreamAudioDestinationNode | null>(null);

  useEffect(() => {
    let active = true;
    const initStream = async () => {
      try {
        const audioConstraints: any = { 
          echoCancellation: { exact: false }, 
          noiseSuppression: { exact: false }, 
          autoGainControl: { exact: false } 
        };
        if (selectedDevice) audioConstraints.deviceId = { exact: selectedDevice };
        
        const stream = await navigator.mediaDevices.getUserMedia({ audio: audioConstraints });
        if (!active) {
          stream.getTracks().forEach(t => t.stop());
          return;
        }
        streamRef.current = stream;
        loadDevices();
        
        const audioCtx = new AudioContext({ sampleRate: 48000 }); // RNNoise strictly requires 48kHz
        audioContextRef.current = audioCtx;
        const source = audioCtx.createMediaStreamSource(stream);
        
        // Load RNNoise Worklet and Weights
        await audioCtx.audioWorklet.addModule('/rnnoise/worklet.js');
        const rnnoiseWeights = await fetch('/rnnoise/rnnoise.bin').then(r => r.arrayBuffer());
        
        if (!active) return; // Prevent race conditions if unmounted during fetch

        const rnnoiseNode = new AudioWorkletNode(audioCtx, 'neural-denoise', {
          processorOptions: { weights: rnnoiseWeights, limit: 16 }
        });
        
        const hp = audioCtx.createBiquadFilter(); hp.type = "highpass";
        const mud = audioCtx.createBiquadFilter(); mud.type = "peaking";
        const pres = audioCtx.createBiquadFilter(); pres.type = "peaking";
        const deEss = audioCtx.createBiquadFilter(); deEss.type = "highshelf";
        const limiter = audioCtx.createDynamicsCompressor();
        
        eqNodesRef.current = { hp, mud, pres, deEss, limiter };
        applyEqPreset(eqPreset, eqNodesRef.current);
        
        // Path A: Meter (Pre-DSP)
        const analyser = audioCtx.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.5;
        source.connect(analyser);
        analyserRef.current = analyser;
        
        const dataArray = new Float32Array(analyser.frequencyBinCount);
        
        const updateMeterFrame = () => {
          if (!active || !analyserRef.current) return;
          analyser.getFloatTimeDomainData(dataArray);

          let maxPeak = 0;
          for (let i = 0; i < dataArray.length; i++) {
            const absValue = Math.abs(dataArray[i]);
            if (absValue > maxPeak) {
              maxPeak = absValue;
            }
          }

          const minDb = -60;
          let peakDb = minDb;
          if (maxPeak > 0) {
            peakDb = 20 * Math.log10(maxPeak);
          }
          peakDb = Math.max(minDb, Math.min(peakDb, 0));

          const meterPercentage = ((peakDb - minDb) / Math.abs(minDb)) * 100;
          
          if (meterFillRef.current) {
            meterFillRef.current.style.width = `${meterPercentage}%`;
            meterFillRef.current.style.background = meterPercentage > 90 ? '#ef4444' : meterPercentage > 70 ? '#eab308' : '#22c55e';
          }
          
          // Peak hold
          const now = performance.now();
          if (meterPercentage > peakHoldRef.current.val || now - peakHoldRef.current.time > 1000) {
            peakHoldRef.current = { val: meterPercentage, time: now };
          }
          if (peakRef.current) {
            peakRef.current.style.left = `${peakHoldRef.current.val}%`;
            const age = now - peakHoldRef.current.time;
            peakRef.current.style.opacity = age > 800 ? `${1 - (age - 800)/200}` : '1';
          }
          
          reqAnimRef.current = requestAnimationFrame(updateMeterFrame);
        };
        
        updateMeterFrame();

        // Path B: Recorder (Processed)
        source.connect(rnnoiseNode);
        rnnoiseNode.connect(hp);
        hp.connect(mud);
        mud.connect(pres);
        pres.connect(deEss);
        deEss.connect(limiter);
        
        const dest = audioCtx.createMediaStreamDestination();
        limiter.connect(dest);
        destRef.current = dest;
      } catch (e) {
        setMicError("Microphone access denied. Please allow microphone access in your browser settings to record voiceovers.");
      }
    };
    
    initStream();
    
    return () => {
      active = false;
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') audioContextRef.current.close();
      cancelAnimationFrame(reqAnimRef.current);
      analyserRef.current = null;
      if (meterFillRef.current) meterFillRef.current.style.width = '0%';
      if (peakRef.current) peakRef.current.style.left = '0%';
    };
  }, [selectedDevice]);

  const startRecording = async () => {
    try {
      setMicError(null);
      if (!destRef.current) throw new Error("No stream");
      
      const recorder = new MediaRecorder(destRef.current.stream, { mimeType: 'audio/webm;codecs=opus' });
      mediaRecorder.current = recorder;
      chunks.current = [];
      
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.current.push(e.data);
      };
      
      recorder.onstop = async () => {
        const blob = new Blob(chunks.current, { type: 'audio/webm' });
        const url = URL.createObjectURL(blob);
        setAudioUrl(url);
        
        // Upload (through the shared API client, which attaches the Clerk session token / cookies)
        try {
          const newBundle = await api.uploadStepAudio(step.id, blob);
          setBundle(newBundle);
        } catch (e) {
          console.error("Audio upload failed", e);
          setMicError(`Couldn't save the voiceover: ${e instanceof Error ? e.message : "upload failed"}`);
        }
      };
      
      recorder.start(1000);
      setIsRecording(true);
    } catch (e) {
      setMicError("Microphone access denied. Please allow microphone access in your browser settings to record voiceovers.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorder.current && isRecording) {
      mediaRecorder.current.stop();
      setIsRecording(false);
    }
  };

  const deleteAudio = async () => {
    setAudioUrl(null);
    setTranscriptText("");
    const newBundle = await api.updateStep(step.id, { audioKey: null, transcript: null, audioReviewed: false });
    setBundle(newBundle);
  };
  
  const saveTranscript = async () => {
    const newTranscript = { ...step.transcript, text: transcriptText };
    const newBundle = await api.updateStep(step.id, { transcript: newTranscript, audioReviewed: true });
    setBundle(newBundle);
  };

  return (
    <div className="field">
      <label>Voiceover</label>
      {micError && (
        <div className="alert alert-danger mb-2" role="alert">
          <p className="small text-danger">{micError}</p>
        </div>
      )}
      
      {!isRecording && !audioUrl && devices.length > 0 && (
        <div className="mb-2">
          <label className="small muted">Microphone</label>
          <select 
            className="input w-full" 
            value={selectedDevice}
            onChange={(e) => {
              setSelectedDevice(e.target.value);
              localStorage.setItem("voiceover_mic", e.target.value);
            }}
          >
            <option value="">Default Microphone</option>
            {devices.map(d => (
              <option key={d.deviceId} value={d.deviceId}>
                {d.label || `Microphone (${d.deviceId.slice(0, 5)}...)`}
              </option>
            ))}
          </select>
        </div>
      )}

      {!audioUrl && (
        <div className="mb-2">
          <div style={{ position: 'relative', height: '10px', background: '#333', borderRadius: '5px' }}>
            <div 
              ref={meterFillRef}
              style={{ 
                height: '100%', 
                width: '0%',
                background: '#22c55e',
                borderRadius: '5px',
                transition: 'background 0.1s ease-out'
              }} 
            />
            <div 
              ref={peakRef}
              style={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                width: '2px',
                background: '#fff',
                left: '0%',
                transition: 'opacity 0.2s ease-out'
              }}
            />
          </div>
        </div>
      )}

      {!audioUrl && !isRecording && (
        <div className="mb-4">
          <label className="small muted">Voice Profile</label>
          <div className="d-flex" style={{ gap: "16px", marginTop: "4px" }}>
            <label style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer", fontSize: "14px" }}>
              <input type="radio" name="eqPreset" value="male" checked={eqPreset === 'male'} onChange={() => { setEqPreset('male'); localStorage.setItem("voiceover_eq", "male"); }} />
              Male Voice
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer", fontSize: "14px" }}>
              <input type="radio" name="eqPreset" value="female" checked={eqPreset === 'female'} onChange={() => { setEqPreset('female'); localStorage.setItem("voiceover_eq", "female"); }} />
              Female Voice
            </label>
          </div>
        </div>
      )}

      {!audioUrl && !isRecording && (
        <button type="button" className="btn btn-secondary" onClick={startRecording}>
          Start Recording
        </button>
      )}
      {isRecording && (
        <button type="button" className="btn btn-primary" onClick={stopRecording}>
          Stop Recording
        </button>
      )}
      {audioUrl && !isRecording && (
        <div className="stack-s">
          <audio src={audioUrl} controls className="w-full" />
          <button type="button" className="btn btn-secondary text-danger" onClick={deleteAudio}>
            Delete Audio
          </button>
          {step.transcript && (
            <div className="stack-xs mt-2">
              <label className="small">Transcript (Auto-generated)</label>
              <textarea 
                value={transcriptText} 
                onChange={(e) => setTranscriptText(e.target.value)} 
                className="w-full" 
                rows={4} 
              />
              {!audioReviewed && <p className="small text-danger">⚠️ Review before publishing</p>}
              <button type="button" className="btn btn-secondary" onClick={saveTranscript}>
                Confirm Transcript
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
